import { ChangeEvent } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DailyExpenseRoute, ExpenseGroup } from "../../types/daily-expense.type";
import { AMOUNT_ERROR_TEXT, EMPTY_CELL_TEXT } from "./constants";
import { GROUP_LABEL, OtherRow, OtherRowErrors } from "./dailyExpenseForm";

/** กลุ่ม · สาย · คำอธิบาย · จำนวนเงิน · ลบ — shared with the column headings above the rows. */
export const OTHER_ROW_COLUMNS = "170px 200px minmax(0, 1fr) 170px 40px";

const GROUPS: ExpenseGroup[] = ["SALES", "ADMIN"];
const NO_GROUP = "";
const NO_ROUTE = "";
const DESCRIPTION_PLACEHOLDER = "เช่น ค่าซ่อมยางรถ";
const AMOUNT_PLACEHOLDER = "0.00";
const SELECT_SLOT_PROPS = { select: { displayEmpty: true } } as const;

interface OtherExpenseRowProps {
  row: OtherRow;
  /** null until the user tries to save, or when the row has nothing wrong. */
  errors: OtherRowErrors | null;
  routes: DailyExpenseRoute[];
  readOnly: boolean;
  onChange: (id: number, patch: Partial<OtherRow>) => void;
  onBlurAmount: (id: number) => void;
  onRemove: (id: number) => void;
}

const isExpenseGroup = (value: string): value is ExpenseGroup =>
  GROUPS.some((group) => group === value);

/**
 * One "อื่นๆ" line: the branch picks its group, may name a route for a ฝ่ายขาย line only, and must
 * say what it was. Switching to ฝ่ายบริหาร drops the route — that line belongs to the whole branch.
 */
export default function OtherExpenseRow({
  row,
  errors,
  routes,
  readOnly,
  onChange,
  onBlurAmount,
  onRemove,
}: Readonly<OtherExpenseRowProps>) {
  const isSales = row.group === "SALES";
  const handleGroupChange = (event: ChangeEvent<HTMLInputElement>) => {
    const group = isExpenseGroup(event.target.value) ? event.target.value : NO_GROUP;
    onChange(row.id, group === "SALES" ? { group } : { group, routeUuid: NO_ROUTE });
  };
  const handleRouteChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(row.id, { routeUuid: event.target.value });
  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(row.id, { description: event.target.value });
  const handleAmountChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange(row.id, { amountText: event.target.value });
  const handleAmountBlur = () => onBlurAmount(row.id);
  const handleRemove = () => onRemove(row.id);
  const fieldSx = { "& .MuiInputBase-root": { bgcolor: readOnly ? "action.hover" : "background.paper" } };

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: OTHER_ROW_COLUMNS, gap: 1.5, alignItems: "start" }}>
      <TextField
        select
        size="small"
        value={row.group}
        onChange={handleGroupChange}
        disabled={readOnly}
        error={Boolean(errors?.group)}
        helperText={errors?.group ? "กรุณาเลือกกลุ่ม" : undefined}
        slotProps={SELECT_SLOT_PROPS}
      >
        <MenuItem value={NO_GROUP}>เลือกกลุ่ม</MenuItem>
        {GROUPS.map((group) => (
          <MenuItem key={group} value={group}>
            {GROUP_LABEL[group]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        size="small"
        value={row.routeUuid}
        onChange={handleRouteChange}
        disabled={readOnly || !isSales}
        slotProps={SELECT_SLOT_PROPS}
      >
        <MenuItem value={NO_ROUTE}>{isSales ? "เลือกสาย (ไม่บังคับ)" : EMPTY_CELL_TEXT}</MenuItem>
        {routes.map((route) => (
          <MenuItem key={route.route_uuid} value={route.route_uuid}>
            {route.route_code}-{route.route_name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        value={row.description}
        onChange={handleDescriptionChange}
        placeholder={DESCRIPTION_PLACEHOLDER}
        error={Boolean(errors?.description)}
        helperText={errors?.description ? "กรุณาใส่คำอธิบาย" : undefined}
        slotProps={{ htmlInput: { readOnly } }}
        sx={fieldSx}
      />
      <TextField
        size="small"
        value={row.amountText}
        onChange={handleAmountChange}
        onBlur={handleAmountBlur}
        placeholder={AMOUNT_PLACEHOLDER}
        error={Boolean(errors?.amount)}
        helperText={errors?.amount ? AMOUNT_ERROR_TEXT : undefined}
        slotProps={{
          htmlInput: { inputMode: "decimal", readOnly, style: { textAlign: "right" } },
        }}
        sx={fieldSx}
      />
      {!readOnly && (
        <Tooltip title="ลบรายการ">
          <IconButton onClick={handleRemove} aria-label="ลบรายการ">
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
